import React, { useEffect, useState } from "react";
import {
  getEventList,
  deleteTrackEvent,
  getCampaignNames,
} from "../../api/Calling";
import { toast } from "react-toastify";
import formatDateTime from "../../components/formatDateTime";
import {
  FaTrash,
  FaExclamationTriangle,
  FaFilter,
} from "react-icons/fa";
import { MdClose, MdList } from "react-icons/md";
import { BiTrendingUp } from "react-icons/bi";

/* 🔹 Custom Hook for Debounce */
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const EventTracker = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  /* Pagination & Search State */
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1,
  });

  /* Modals State */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  /* Filters */
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [filterCampaign, setFilterCampaign] = useState("all");
  // campaignOptions = unique camp_name strings for the dropdown
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  /* ================= FETCH ================= */
  const fetchData = async () => {
    setLoading(true);
    try {
      let params = `?page=${pagination.page}&limit=${pagination.limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;

      if (debouncedSearch) {
        params += `&search=${debouncedSearch}`;
      }

      if (filterStatus !== "all") {
        params += `&status=${filterStatus}`;
      }

      if (filterSource !== "all") {
        params += `&source=${filterSource}`;
      }

      if (filterCampaign !== "all") {
        params += `&camp_name=${filterCampaign}`;
      }

      const resp = await getEventList(params);

      if (resp?.success && Array.isArray(resp.data)) {
        setData(resp.data);
        setPagination({
          page: resp.page,
          limit: resp.limit,
          total: resp.total,
          totalPages: Math.ceil(resp.total / resp.limit),
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [pagination.page, pagination.limit, debouncedSearch, filterStatus, filterSource, filterCampaign, sortBy, sortOrder]);

  /* 🔹 Fetch Campaign Names
     API now returns [{ camp_name, p_id }, ...] — extract unique camp_names for dropdown */
  const fetchCampaigns = async () => {
    try {
      const resp = await getCampaignNames();
      if (resp?.status && Array.isArray(resp.data)) {
        // resp.data = [{ camp_name: "P1", p_id: "..." }, ...]
        const uniqueNames = [...new Set(resp.data.map((c) => c.camp_name).filter(Boolean))];
        setCampaignOptions(uniqueNames);
      }
    } catch (err) {
      console.error("Failed to fetch campaigns", err);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  /* ================= DELETE CONFIRMATION ================= */
  const openDeleteConfirmation = (ids) => {
    setDeleteTarget(ids);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const resp = await deleteTrackEvent({ ids: deleteTarget });

      if (resp?.success) {
        toast.success(resp.message || "Deleted successfully");
        setSelectedIds([]);
        fetchData();
      } else {
        toast.error(resp?.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  /* ================= CHECKBOX HANDLING ================= */
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(data.map((item) => item._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((item) => item !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  return (
    <div className="w-full md:ml-[4.4rem] px-4 md:px-8 mt-20 bg-main-bg min-h-screen pb-12">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

        * {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .mono {
          font-family: 'JetBrains Mono', monospace;
        }

        .glass-effect {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        .stat-card {
          background: linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%);
          position: relative;
          overflow: hidden;
        }

        .stat-card-success {
          background: linear-gradient(135deg, #10B981 0%, #059669 100%);
        }

        .stat-card-organic {
          background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
        }

        .stat-card-pid {
          background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
        }
      `}</style>

      {/* HEADER SECTION */}
      <div className="mb-10">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 mb-8">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-main-blue rounded-xl shadow-lg">
                <BiTrendingUp className="text-2xl text-main-white" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-main-font tracking-tight">
                Event Tracker Management
              </h1>
            </div>
            <p className="text-main-subfont text-base lg:text-lg font-medium ml-16">
              View and manage all tracked events
            </p>
          </div>
        </div>

        {/* STATS CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="stat-card text-main-white p-6 rounded-2xl shadow-lg overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
            <div className="relative z-10">
              <p className="text-sm font-medium opacity-90 mb-1">Total Events</p>
              <p className="text-4xl font-bold">{pagination.total}</p>
            </div>
          </div>

          <div className="stat-card-success text-main-white p-6 rounded-2xl shadow-lg overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
            <div className="relative z-10">
              <p className="text-sm font-medium opacity-90 mb-1">Success</p>
              <p className="text-4xl font-bold">
                {data.filter((item) => item.status === "success").length}
              </p>
            </div>
          </div>

          <div className="stat-card-organic text-main-white p-6 rounded-2xl shadow-lg overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
            <div className="relative z-10">
              <p className="text-sm font-medium opacity-90 mb-1">Organic</p>
              <p className="text-4xl font-bold">
                {data.filter((item) => item.source === "organic").length}
              </p>
            </div>
          </div>

          <div className="stat-card-pid text-main-white p-6 rounded-2xl shadow-lg overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
            <div className="relative z-10">
              <p className="text-sm font-medium opacity-90 mb-1">PID Source</p>
              <p className="text-4xl font-bold">
                {data.filter((item) => item.source === "pid").length}
              </p>
            </div>
          </div>
        </div>

        {/* FILTERS & SEARCH */}
        <div className="bg-main-white rounded-2xl shadow-lg border border-main-border p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-main-blue/10 rounded-lg">
              <FaFilter className="text-main-blue text-lg" />
            </div>
            <h2 className="text-2xl font-bold text-main-font">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <input
                type="text"
                placeholder="Search Email, PID, or Device ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mono w-full px-4 py-3 border-2 border-main-border rounded-xl text-base
                         focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                         transition-all duration-200"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                       focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                       transition-all duration-200"
            >
              <option value="all">All Status</option>
              <option value="success">Success Only</option>
              <option value="fail">Fail Only</option>
            </select>

            {/* Source Filter */}
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                       focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                       transition-all duration-200"
            >
              <option value="all">All Sources</option>
              <option value="organic">Organic Only</option>
              <option value="pid">PID Only</option>
            </select>

            {/* Campaign Filter — uses unique camp_name strings */}
            <select
              value={filterCampaign}
              onChange={(e) => setFilterCampaign(e.target.value)}
              className="px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                       focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                       transition-all duration-200"
            >
              <option value="all">All Campaigns</option>
              {campaignOptions.map((campName, idx) => (
                <option key={idx} value={campName}>
                  {campName}
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="mt-4">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                       focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                       transition-all duration-200"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>

          {/* Bulk Actions */}
          {selectedIds.length > 0 && (
            <div className="mt-4 flex items-center justify-between p-4 bg-main-blue/10 rounded-xl border-2 border-main-blue/20">
              <span className="text-main-font font-semibold">
                {selectedIds.length} item(s) selected
              </span>
              <button
                onClick={() => openDeleteConfirmation(selectedIds)}
                className="bg-main-delete text-main-white px-6 py-2 rounded-xl font-semibold 
                         hover:bg-main-delete/90 transition-all duration-200 flex items-center gap-2"
              >
                <FaTrash />
                Delete Selected
              </button>
            </div>
          )}
        </div>
      </div>

      {/* TABLE SECTION */}
      <div className="bg-main-white rounded-3xl shadow-2xl border border-main-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-main-th border-b-2 border-main-border">
                <th className="p-5 text-center w-16">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === data.length && data.length > 0}
                    onChange={handleSelectAll}
                    className="w-5 h-5 cursor-pointer"
                  />
                </th>
                <th className="p-5 text-left">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-6 bg-main-blue rounded-full"></div>
                    <span className="text-main-font font-bold text-sm uppercase tracking-wide">Email</span>
                  </div>
                </th>
                <th className="p-5 text-left">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">PID</span>
                </th>
                <th className="p-5 text-left">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">Campaign</span>
                </th>
                <th className="p-5 text-left">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">Order ID</span>
                </th>
                <th className="p-5 text-left">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">Device ID</span>
                </th>
                <th className="p-5 text-center">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">Event</span>
                </th>
                <th className="p-5 text-center">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">Status</span>
                </th>
                <th className="p-5 text-center">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">Source</span>
                </th>
                <th className="p-5 text-center">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">Created At</span>
                </th>
                <th className="p-5 text-center">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">Actions</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="p-16">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <span className="w-12 h-12 border-4 border-main-blue/30 border-t-main-blue rounded-full animate-spin"></span>
                      <p className="text-main-subfont font-medium animate-pulse">Loading...</p>
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((row, index) => (
                  <tr
                    key={row._id}
                    className="border-b border-main-border/50 hover:bg-main-thHover transition-all duration-200 group"
                  >
                    <td className="p-5 text-center">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row._id)}
                        onChange={() => handleSelectOne(row._id)}
                        className="w-5 h-5 cursor-pointer"
                      />
                    </td>

                    <td className="p-5">
                      <span className="text-main-font font-semibold text-base">
                        {row.email_id || "N/A"}
                      </span>
                    </td>

                    <td className="p-5">
                      <span className="mono text-main-font font-semibold text-base px-3 py-1.5 bg-main-bg rounded-lg">
                        {row.p_id || "N/A"}
                      </span>
                    </td>

                    <td className="p-5">
                      <span className="text-main-font font-semibold text-base">
                        {row.camp_name || "N/A"}
                      </span>
                    </td>

                    <td className="p-5">
                      <span className="mono text-main-font font-semibold text-base px-3 py-1.5 bg-main-bg rounded-lg">
                        {row.e_order_id || "N/A"}
                      </span>
                    </td>

                    <td className="p-5">
                      <span className="mono text-main-subfont text-sm px-2 py-1 bg-gray-50 rounded">
                        {row.device_id || "N/A"}
                      </span>
                    </td>

                    <td className="p-5">
                      <div className="flex justify-center">
                        <span className="px-4 py-2 bg-main-blue/10 text-main-blue font-semibold text-sm rounded-xl">
                          {row.event}
                        </span>
                      </div>
                    </td>

                    <td className="p-5">
                      <div className="flex justify-center">
                        <span className={`px-4 py-2 font-semibold text-sm rounded-xl ${row.status === "success"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                          }`}>
                          {row.status}
                        </span>
                      </div>
                    </td>

                    <td className="p-5">
                      <div className="flex justify-center">
                        <span className={`px-4 py-2 font-semibold text-sm rounded-xl ${row.source === "organic"
                          ? "bg-purple-100 text-purple-700"
                          : "bg-amber-100 text-amber-700"
                          }`}>
                          {row.source}
                        </span>
                      </div>
                    </td>

                    <td className="p-5">
                      <div className="text-center space-y-1">
                        <div className="text-main-font text-sm font-semibold mono">
                          {formatDateTime(row.createdAt, "DD/MM/YYYY")}
                        </div>
                        <div className="text-main-subfont text-xs mono">
                          {formatDateTime(row.createdAt, "hh:mm:ss A")}
                        </div>
                      </div>
                    </td>

                    <td className="p-5">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => openDeleteConfirmation([row._id])}
                          className="text-main-delete font-semibold hover:bg-main-delete/10 px-5 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-2 border-2 border-transparent hover:border-main-delete/20"
                        >
                          <FaTrash className="text-xs" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}

              {!loading && data.length === 0 && (
                <tr>
                  <td colSpan="11" className="p-16">
                    <div className="flex flex-col items-center gap-4">
                      <div className="p-6 bg-main-bg rounded-full">
                        <BiTrendingUp className="text-6xl text-main-subfont/30" />
                      </div>
                      <div className="text-center space-y-2">
                        <p className="text-main-font font-semibold text-xl">No events found</p>
                        <p className="text-main-subfont text-base max-w-md">
                          {search || filterStatus !== "all" || filterSource !== "all"
                            ? "Try adjusting your filters"
                            : "No events have been tracked yet"}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {pagination.totalPages > 1 && (
          <div className="p-6 border-t-2 border-main-border flex justify-between items-center">
            <span className="text-main-subfont text-sm font-medium">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
              {pagination.total} entries
            </span>

            <div className="flex gap-2">
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
                className="px-4 py-2 rounded-xl border-2 border-main-border font-semibold text-sm
                         disabled:opacity-40 disabled:cursor-not-allowed hover:bg-main-blue hover:text-main-white
                         hover:border-main-blue transition-all duration-200"
              >
                Previous
              </button>

              <div className="flex gap-1">
                {[...Array(pagination.totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPagination({ ...pagination, page: i + 1 })}
                    className={`w-10 h-10 rounded-xl font-bold text-sm transition-all duration-200 ${pagination.page === i + 1
                      ? "bg-main-blue text-main-white"
                      : "border-2 border-main-border hover:bg-main-blue/10"
                      }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page === pagination.totalPages}
                className="px-4 py-2 rounded-xl border-2 border-main-border font-semibold text-sm
                         disabled:opacity-40 disabled:cursor-not-allowed hover:bg-main-blue hover:text-main-white
                         hover:border-main-blue transition-all duration-200"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-main-white w-full max-w-lg rounded-3xl shadow-2xl border-2 border-main-border overflow-hidden">
            <div className="bg-gradient-to-r from-main-delete to-main-delete/90 p-8 text-main-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-main-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10 flex flex-col items-center text-center gap-4">
                <div className="p-5 bg-main-white/20 rounded-2xl backdrop-blur-sm">
                  <FaExclamationTriangle className="text-5xl" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-2">Confirm Deletion</h2>
                  <p className="text-main-white/90 text-sm">This action cannot be undone</p>
                </div>
              </div>
            </div>

            <div className="p-8">
              <div className="bg-main-bg/50 border-2 border-main-border rounded-2xl p-6 mb-6">
                <p className="text-main-font text-center leading-relaxed">
                  You are about to delete{" "}
                  <span className="mono font-bold text-main-blue">
                    {deleteTarget?.length || 0}
                  </span>{" "}
                  {deleteTarget?.length === 1 ? "event" : "events"}.
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteTarget(null);
                  }}
                  className="flex-1 px-6 py-4 text-main-subfont font-bold hover:text-main-font 
                           hover:bg-main-bg rounded-2xl transition-all duration-200 border-2 border-main-border"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-main-delete text-main-white px-6 py-4 rounded-2xl font-bold 
                           hover:bg-main-delete/90 shadow-lg hover:shadow-xl transition-all duration-300 
                           flex items-center justify-center gap-2"
                >
                  <FaTrash />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventTracker;